/**
 * Responsibility: describe the industrial biome — flat ground, a block grid of heavy plant, and a transmission
 * network laced through it.
 *
 * ## This was an installation first, and that was the wrong shape
 *
 * The first version scattered self-contained works across open ground, a few hundred units across and rare. Flown, it
 * read as props dotted about rather than as *anywhere*. What was wanted is a region like the city: you cross into it
 * and everything around you for a minute is industry. So the parts survived unchanged and the placement was thrown
 * away — a block grid at four to five times the city's size, filling a biome, with everything scaled to city height.
 *
 * ## The grid is the city's, with one difference that decides the whole layout
 *
 * The city rolls one building per block. This rolls one **use** per block, and a use is a group: three cooling
 * towers, a tank farm on its own grid, a row of spheres. That is what makes an estate read as an estate rather than
 * as evenly spaced objects.
 *
 * `yard` — a block with nothing on it — is not padding. A wall-to-wall estate has no silhouette because nothing has
 * sky behind it, and the gaps are what let the cooling towers read.
 *
 * ## The transmission network, and why every line ends at something
 *
 * A line that fades out mid-air reads as unfinished. So:
 *
 * - Substations sit on a coarse lattice: every block whose indices are both multiples of `SUBSTATION_EVERY`.
 * - A block on a row or column *between* two existing substations is a **line corridor** and holds one pylon.
 * - A corridor exists only when the substations at both ends of its run do, which makes every line terminate at a
 *   substation **by construction** rather than by checking.
 *
 * At the edge of the region the substations stop existing, so the corridors do too, and the last substation simply
 * has nothing leaving it. No dangling pylons, and no routing code.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createLocalRng, deriveSeed } from '../rng.js';
import {
  chimneySolid,
  conductorAnchors,
  conductorSegments,
  coolingTowerSolid,
  pipeRackSegments,
  pylonSegments,
  shedSegments,
  sphereSkirtSegments,
  sphereSolid,
  substationAnchors,
  substationSegments,
  tankSolid,
} from '../industrialGeometry.js';
import { INDUSTRIAL, PALETTE, INDUSTRIAL_PALETTE } from '../../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/** An integer in a range, from a 0..1 roll. */
function countFrom(roll, min, max) {
  return min + Math.min(max - min, Math.floor(roll * (max - min + 1)));
}

const BLOCK = INDUSTRIAL.BLOCK_SIZE;

/** Usable half-width inside a block, once the roadway around it is taken out. */
const INNER_HALF = (BLOCK - INDUSTRIAL.ROAD_WIDTH) / 2;

/**
 * Whether another biome has reserved any of a block's ground.
 *
 * A grid of small probes, not one wide query. A clearance test does not scale by radius — the water clearance checks a
 * centre and a *ring* at whatever it is given and sees nothing in between — which is how suburbia's streets ended up
 * drawn across a lake. Farmland's fields reached the same answer first.
 *
 * ## The grid had to be measured, not chosen
 *
 * A three-by-three grid, which is what suburbia uses over a much smaller block, leaves a hole here: laid over the water
 * clearance's own centre-plus-ring pattern, the furthest any point in the block can be from something actually sampled
 * came out at **80 units**. A substation stood with a corner in a lake at 83 units off its block centre, and the
 * nothing-stands-in-water test caught it.
 *
 * Measured across the block for several layouts, worst hole in world units: 3x3 gives 80, 5x5 gives 42, and this
 * seven-by-seven with a radius of a third of the spacing gives 24. The radius is tied to the spacing rather than being
 * its own number, so changing `CLEARANCE_PROBES` cannot silently reopen the hole.
 */
function blockIsReserved(centreX, centreZ, isReserved) {
  if (typeof isReserved !== 'function') return false;

  const probes = INDUSTRIAL.CLEARANCE_PROBES;
  const spacing = (2 * INNER_HALF) / (probes - 1);
  const reach = spacing / 3 + INDUSTRIAL.CLEARANCE;

  for (let i = 0; i < probes; i += 1) {
    for (let j = 0; j < probes; j += 1) {
      const x = centreX - INNER_HALF + spacing * i;
      const z = centreZ - INNER_HALF + spacing * j;
      if (isReserved(x, z, reach)) return true;
    }
  }

  return false;
}


/** Range of block indices whose centres fall inside a span. */
function blockRange(start, length) {
  return {
    first: Math.ceil(start / BLOCK - 0.5),
    last: Math.ceil((start + length) / BLOCK - 0.5) - 1,
  };
}

/** Whether a block index pair is a substation position on the coarse lattice. */
function isSubstationSlot(blockX, blockZ) {
  const every = INDUSTRIAL.SUBSTATION_EVERY;
  return ((blockX % every) + every) % every === 0 && ((blockZ % every) + every) % every === 0;
}

/** Centre of a block, in world units. */
function blockCentre(blockX, blockZ) {
  return [(blockX + 0.5) * BLOCK, (blockZ + 0.5) * BLOCK];
}

/** Which use a roll calls for, from the configured weights. */
function useFor(roll) {
  const weights = INDUSTRIAL.USE_WEIGHTS;
  const names = Object.keys(weights);
  const total = names.reduce((sum, name) => sum + weights[name], 0);

  let running = 0;
  for (const name of names) {
    running += weights[name] / total;
    if (roll < running) return name;
  }

  return names[names.length - 1];
}

/**
 * A ring-stack part, in the shape the `industrialSolid` kind consumes.
 *
 * One helper because four different shapes all need the same envelope, and getting `rings` or `sides` out of step
 * with the solid they describe would overrun the buffer `tileContent` sized for it.
 */
function solidPart(solid, groundY, height, rings, sides, part) {
  return {
    kind: 'industrialSolid',
    x: solid.rings[0].points[0][0],
    z: solid.rings[0].points[0][2],
    solid,
    groundY,
    height,
    rings,
    sides,
    part,
  };
}

/**
 * What a block holds, given its use.
 *
 * Each branch lays its group out inside `INNER_HALF` of the block centre, so the roadway between blocks stays clear
 * and the grid reads from the air.
 *
 * @returns {{solids: Array<object>, lines: Array<object>}}
 */
function layOutBlock(use, centreX, centreZ, random, sampleGround) {
  const solids = [];
  const lines = [];

  const facing = random() * Math.PI * 2;
  const alongX = Math.cos(facing);
  const alongZ = Math.sin(facing);

  /** A point in the block's own rotated frame. */
  const at = (along, across) => [centreX + alongX * along - alongZ * across, centreZ + alongZ * along + alongX * across];

  if (use === 'cooling') {
    const count = countFrom(random(), INDUSTRIAL.MIN_COOLING_TOWERS, INDUSTRIAL.MAX_COOLING_TOWERS);

    for (let i = 0; i < count; i += 1) {
      /*
        A pair stands side by side; three stand on a circle rather than in a row. That is a fit problem before it is a
        look one — three in a line span some 670 units and overrun a 620-unit block into the roadway. See
        `COOLING_GROUP_RADIUS`.
      */
      let along;
      let across;

      if (count <= 2) {
        along = (i - (count - 1) / 2) * INDUSTRIAL.COOLING_SPACING;
        across = 0;
      } else {
        const angle = (i / count) * Math.PI * 2;
        along = Math.cos(angle) * INDUSTRIAL.COOLING_GROUP_RADIUS;
        across = Math.sin(angle) * INDUSTRIAL.COOLING_GROUP_RADIUS;
      }

      const [x, z] = at(along, across);
      const groundY = sampleGround(x, z);

      solids.push(
        solidPart(
          // Spun per tower, so a row is not a set of vertex-aligned copies.
          coolingTowerSolid({ x, z, groundY, spin: (i * 0.37) % 1 }),
          groundY,
          INDUSTRIAL.COOLING_HEIGHT,
          INDUSTRIAL.COOLING_RINGS,
          INDUSTRIAL.COOLING_SIDES,
          'coolingTower',
        ),
      );
    }

    return { solids, lines };
  }

  if (use === 'tanks') {
    const count = countFrom(random(), INDUSTRIAL.MIN_TANKS, INDUSTRIAL.MAX_TANKS);
    const perRow = Math.ceil(Math.sqrt(count));

    for (let i = 0; i < count; i += 1) {
      const along = ((i % perRow) - (perRow - 1) / 2) * INDUSTRIAL.TANK_SPACING;
      const across = (Math.floor(i / perRow) - (perRow - 1) / 2) * INDUSTRIAL.TANK_SPACING;

      const [x, z] = at(along, across);
      const groundY = sampleGround(x, z);
      const radius = lerp(INDUSTRIAL.MIN_TANK_RADIUS, INDUSTRIAL.MAX_TANK_RADIUS, random());

      solids.push(
        solidPart(
          tankSolid({ x, z, groundY, radius }),
          groundY,
          radius * INDUSTRIAL.TANK_HEIGHT_RATIO,
          INDUSTRIAL.TANK_RINGS,
          INDUSTRIAL.TANK_SIDES,
          'tank',
        ),
      );
    }

    return { solids, lines };
  }

  if (use === 'spheres') {
    const count = countFrom(random(), INDUSTRIAL.MIN_SPHERES, INDUSTRIAL.MAX_SPHERES);

    for (let i = 0; i < count; i += 1) {
      const offset = (i - (count - 1) / 2) * INDUSTRIAL.SPHERE_SPACING;
      const [x, z] = at(offset, 0);
      const groundY = sampleGround(x, z);

      const { solid, lift, radius } = sphereSolid({ x, z, groundY });

      solids.push(
        solidPart(solid, groundY + lift, radius * 2, INDUSTRIAL.SPHERE_RINGS, INDUSTRIAL.SPHERE_SIDES, 'sphere'),
      );
      lines.push(...sphereSkirtSegments({ x, z, groundY }));
    }

    return { solids, lines };
  }

  if (use === 'stacks') {
    const count = countFrom(random(), INDUSTRIAL.MIN_STACKS, INDUSTRIAL.MAX_STACKS);

    for (let i = 0; i < count; i += 1) {
      const offset = (i - (count - 1) / 2) * INDUSTRIAL.STACK_SPACING;
      const [x, z] = at(offset, 0);
      const groundY = sampleGround(x, z);

      solids.push(
        solidPart(
          chimneySolid({ x, z, groundY }),
          groundY,
          INDUSTRIAL.CHIMNEY_HEIGHT,
          INDUSTRIAL.CHIMNEY_RINGS,
          INDUSTRIAL.CHIMNEY_SIDES,
          'chimney',
        ),
      );
    }

    // A shed at the foot of the stacks, so they are attached to something rather than standing alone.
    const [shedX, shedZ] = at(0, INDUSTRIAL.SHED_WIDTH * 1.3);
    lines.push(...shedSegments({ x: shedX, z: shedZ, groundY: sampleGround(shedX, shedZ), facing }));

    return { solids, lines };
  }

  if (use === 'sheds') {
    const count = countFrom(random(), INDUSTRIAL.MIN_SHEDS, INDUSTRIAL.MAX_SHEDS);

    for (let i = 0; i < count; i += 1) {
      const across = (i - (count - 1) / 2) * (INDUSTRIAL.SHED_WIDTH + INDUSTRIAL.SHED_SPACING);
      const [x, z] = at(0, across);
      lines.push(...shedSegments({ x, z, groundY: sampleGround(x, z), facing }));
    }

    return { solids, lines };
  }

  if (use === 'pipes') {
    const [x, z] = at(0, 0);
    lines.push(...pipeRackSegments({ x, z, groundY: sampleGround(x, z), facing }, sampleGround));

    // A tank at each end, so a rack is carrying something to somewhere.
    for (const sign of [-1, 1]) {
      const radius = INDUSTRIAL.MIN_TANK_RADIUS;

      // Offset by the radius of the tank actually built, not the largest one possible — that overran the block.
      const [tx, tz] = at(sign * (INDUSTRIAL.PIPE_RUN_LENGTH / 2 + radius), 0);
      const groundY = sampleGround(tx, tz);

      solids.push(
        solidPart(
          tankSolid({ x: tx, z: tz, groundY, radius }),
          groundY,
          radius * INDUSTRIAL.TANK_HEIGHT_RATIO,
          INDUSTRIAL.TANK_RINGS,
          INDUSTRIAL.TANK_SIDES,
          'tank',
        ),
      );
    }

    return { solids, lines };
  }

  // `yard`: deliberately empty. See the note at the top of this file about silhouette.
  return { solids, lines };
}

/** Ground, gentle and low, so a rectangular grid of heavy plant never sits on a slope. */
function createTerrain(seed) {
  const field = createFbm(seed, 'elevation:industrial', {
    octaves: INDUSTRIAL.OCTAVES,
    baseFrequency: 1 / INDUSTRIAL.WAVELENGTH,
    persistence: INDUSTRIAL.PERSISTENCE,
    lacunarity: INDUSTRIAL.LACUNARITY,
  });

  return function sampleIndustrialElevation(x, z) {
    return toUnitRange(field(x, z)) * INDUSTRIAL.MAX_ELEVATION;
  };
}

export const industrial = {
  id: 'industrial',
  label: 'Industry',

  palette: PALETTE.industrial,

  /**
   * The plant gets a hot amber ramp where the ground stays cold and dim.
   *
   * The same trick the forest uses for its trees and suburbia for its roofs. It matters more here than anywhere: this
   * biome and the city are both grids of tall things, and amber against the city's cold cyan is what keeps them
   * separable at fog distance.
   */
  structurePalette: INDUSTRIAL_PALETTE,

  suitabilityBias: INDUSTRIAL.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number}
   */
  createElevation(seed) {
    return createTerrain(seed);
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: INDUSTRIAL.MAX_ELEVATION,

  /**
   * Build the structure placement function.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    const blockSeed = deriveSeed(seed, 'structures:industrial');

    /**
     * @param {{originX: number, originZ: number, size: number}} region
     * @param {object} context
     */
    return function collectStructures(region, { sampleWeight, sampleGround, sampleGroundDetail, isReserved }) {
      const xRange = blockRange(region.originX, region.size);
      const zRange = blockRange(region.originZ, region.size);

      const structures = [];

      /*
        Buildability, asked once per block and remembered.

        **A block's neighbours need this answer as much as the block does.** A corridor has to know whether the
        substation at the end of its run is really there before it strings a cable to it — and without the memo the
        same block would be re-probed up to five times per tile, at forty-nine clearance queries a go.
      */
      const buildable = new Map();

      /**
       * Whether a block can hold anything at all: inside the region, level enough, and clear of every other biome.
       *
       * Slope and clearance are decided for the whole block, before any roll is spent on its contents.
       */
      const canBuild = (blockX, blockZ) => {
        const key = `${blockX},${blockZ}`;
        const known = buildable.get(key);
        if (known !== undefined) return known;

        const [x, z] = blockCentre(blockX, blockZ);
        const answer =
          sampleWeight(x, z) > 0 &&
          sampleGroundDetail(x, z).slope <= INDUSTRIAL.MAX_GROUND_SLOPE &&
          !blockIsReserved(x, z, isReserved);

        buildable.set(key, answer);
        return answer;
      };

      /**
       * Whether a substation stands at a lattice position.
       *
       * Deliberately **not** subject to a density roll. Density would leave a line with a substation at one end and
       * nothing at the other, which is the exact failure this network exists to avoid — so the trunk is present
       * wherever the biome is, and thins out only by the biome itself thinning out.
       *
       * It *is* subject to buildability, and that is load-bearing rather than incidental: a substation refused for
       * standing in a lake would otherwise still have cables run to it from either side, which is the same dangling
       * line by another route.
       */
      const substationAt = (blockX, blockZ) => isSubstationSlot(blockX, blockZ) && canBuild(blockX, blockZ);

      /**
       * What a block is for, before anything is laid out.
       *
       * Four cases in order: unbuildable, a substation lattice position, a corridor between two of them, or an
       * industrial use.
       */
      const roleOf = (blockX, blockZ) => {
        if (!canBuild(blockX, blockZ)) return 'none';
        if (isSubstationSlot(blockX, blockZ)) return 'substation';

        const every = INDUSTRIAL.SUBSTATION_EVERY;
        const onRow = ((blockZ % every) + every) % every === 0;
        const onColumn = ((blockX % every) + every) % every === 0;

        /*
          A corridor exists only if the substations flanking its run both do. That single condition is what guarantees
          every line terminates at something, with no routing and no search.
        */
        if (onRow) {
          const low = Math.floor(blockX / every) * every;
          if (substationAt(low, blockZ) && substationAt(low + every, blockZ)) return 'corridorX';
        }

        if (onColumn) {
          const low = Math.floor(blockZ / every) * every;
          if (substationAt(blockX, low) && substationAt(blockX, low + every)) return 'corridorZ';
        }

        return 'use';
      };

      for (let blockX = xRange.first; blockX <= xRange.last; blockX += 1) {
        for (let blockZ = zRange.first; blockZ <= zRange.last; blockZ += 1) {
          const [centreX, centreZ] = blockCentre(blockX, blockZ);

          const role = roleOf(blockX, blockZ);
          if (role === 'none') continue;

          const weight = sampleWeight(centreX, centreZ);

          /*
            One stream per block, keyed by block index, so a block does not depend on which tile is being built.
            Roll order is part of the world's identity — append only.
          */
          const random = createLocalRng(blockSeed, blockX, blockZ);
          const useRoll = random();
          const facingRoll = random();

          const groundY = sampleGround(centreX, centreZ);

          if (role === 'substation') {
            const yard = { x: centreX, z: centreZ, groundY, facing: facingRoll * Math.PI * 2 };

            structures.push({
              kind: 'industrialFrame',
              x: centreX,
              z: centreZ,
              lines: substationSegments(yard, sampleGround),
              role: 'substation',
              weight,
            });

            /*
              The spans leaving this substation, drawn by the substation itself in the +x and +z directions only. That
              low-side rule is what stops a span being drawn twice — farmland's hedgerows settled it first.
            */
            for (const [dx, dz] of [
              [1, 0],
              [0, 1],
            ]) {
              const nextX = blockX + dx;
              const nextZ = blockZ + dz;
              const nextRole = roleOf(nextX, nextZ);
              if (nextRole !== 'corridorX' && nextRole !== 'corridorZ') continue;

              const [nx, nz] = blockCentre(nextX, nextZ);
              const heading = Math.atan2(dz, dx);

              const nextPylon = { x: nx, z: nz, groundY: sampleGround(nx, nz), facing: heading };

              structures.push({
                kind: 'industrialFrame',
                x: (centreX + nx) / 2,
                z: (centreZ + nz) / 2,
                lines: conductorSegments(substationAnchors(yard), conductorAnchors(nextPylon)),
                role: 'span',
                weight,
              });
            }

            continue;
          }

          if (role === 'corridorX' || role === 'corridorZ') {
            const heading = role === 'corridorX' ? 0 : Math.PI / 2;
            const pylon = { x: centreX, z: centreZ, groundY, facing: heading };

            structures.push({
              kind: 'industrialFrame',
              x: centreX,
              z: centreZ,
              lines: pylonSegments(pylon),
              role,
              weight,
            });

            /*
              The span forward to whatever comes next along the line — another pylon, or the substation at the end of
              the run. `conductorSegments` takes anchor lists rather than supports, so it does not need to know which.

              **A separate structure from the pylon, not appended to it.** A span is the only thing here that is not
              standing on the ground, and everything that reasons about footprints has to be able to tell the
              difference: cables cross a lake corner two hundred units up, which is correct, while the steelwork
              holding them up must not.
            */
            const nextX = blockX + (role === 'corridorX' ? 1 : 0);
            const nextZ = blockZ + (role === 'corridorZ' ? 1 : 0);
            const nextRole = roleOf(nextX, nextZ);
            if (nextRole !== 'substation' && nextRole !== role) continue;

            const [nx, nz] = blockCentre(nextX, nextZ);
            const far = { x: nx, z: nz, groundY: sampleGround(nx, nz), facing: heading };

            structures.push({
              kind: 'industrialFrame',
              x: (centreX + nx) / 2,
              z: (centreZ + nz) / 2,
              lines: conductorSegments(
                conductorAnchors(pylon),
                nextRole === 'substation' ? substationAnchors(far) : conductorAnchors(far),
              ),
              role: 'span',
              weight,
            });

            continue;
          }

          /*
            An ordinary block. Its use is rolled, and density is applied by *thinning the uses* rather than by skipping
            blocks: a weight below 1 makes `yard` more likely, so the edge of the region is emptier ground rather than
            a hard line — the same thinning-not-fading rule the city and forest follow.
          */
          const use = weight >= 1 || useRoll < weight ? useFor(useRoll / Math.max(weight, 1e-6)) : 'yard';
          if (use === 'yard') continue;

          const laid = layOutBlock(use, centreX, centreZ, random, sampleGround);

          for (const solid of laid.solids) {
            structures.push({ ...solid, use, weight });
          }

          if (laid.lines.length > 0) {
            structures.push({
              kind: 'industrialFrame',
              x: centreX,
              z: centreZ,
              lines: laid.lines,
              role: 'use',
              use,
              weight,
            });
          }
        }
      }

      return structures;
    };
  },

  /**
   * Publish the estate so other biomes keep out of it.
   *
   * A rectangle per block rather than a circle, for the reason suburbia found: blocks tile the plane, so a circle
   * leaves the corners between four blocks unreserved while a rectangle covers the region continuously.
   *
   * Needs no terrain, so it answers at any coordinate for the price of one biome-field sample.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation() {
    return function insideAnEstate(x, z, radius, { sampleWeight, cache }) {
      const margin = radius + INDUSTRIAL.CLEARANCE + BLOCK;

      const firstX = Math.floor((x - margin) / BLOCK);
      const lastX = Math.floor((x + margin) / BLOCK);
      const firstZ = Math.floor((z - margin) / BLOCK);
      const lastZ = Math.floor((z + margin) / BLOCK);

      for (let blockX = firstX; blockX <= lastX; blockX += 1) {
        for (let blockZ = firstZ; blockZ <= lastZ; blockZ += 1) {
          const key = `industrial:${blockX},${blockZ}`;

          // Memoised per tile, and not optional: every tree and rock column in the world asks this once.
          let built = cache?.get(key);

          if (built === undefined) {
            const [cx, cz] = blockCentre(blockX, blockZ);
            built = sampleWeight(cx, cz) > 0;
            cache?.set(key, built);
          }

          if (!built) continue;

          const nearX = Math.max(blockX * BLOCK, Math.min(x, (blockX + 1) * BLOCK));
          const nearZ = Math.max(blockZ * BLOCK, Math.min(z, (blockZ + 1) * BLOCK));

          if (Math.hypot(x - nearX, z - nearZ) < radius + INDUSTRIAL.CLEARANCE) return true;
        }
      }

      return false;
    };
  },
};

/** Exported so tests and budgets can reason about the grid. */
export { BLOCK as INDUSTRIAL_BLOCK_SIZE, isSubstationSlot };
